<template>
  <div class="wa-phone-preview">
    <div class="wa-topbar">
      <div class="avatar">W</div>
      <div>
        <strong>WhatsApp Preview</strong>
        <small>template message</small>
      </div>
    </div>

    <div class="wa-chat">
      <div class="wa-bubble">
        <!-- HEADER -->
        <div v-if="header.type === 'IMAGE'" class="media">
          <img :src="header.url" @error="mediaError = true" />
        </div>

        <div v-else-if="header.type === 'VIDEO'" class="media">
          <video controls>
            <source :src="header.url" type="video/mp4" />
          </video>
        </div>

        <div v-else-if="header.type === 'DOCUMENT'" class="doc-box">
          <div class="doc-icon">📄</div>
          <div>
            <strong>{{ header.filename }}</strong>
            <small>Document attachment</small>
          </div>
        </div>

        <div v-else-if="header.type === 'TEXT'" class="header-text">
          {{ replaceVars(header.text) }}
        </div>

        <div v-if="mediaError" class="media-error">
          Media preview unavailable
        </div>

        <!-- BODY -->
        <div v-if="bodyText" class="body-text" v-html="formattedBody"></div>

        <!-- FOOTER -->
        <div v-if="footerText" class="footer-text">
          {{ footerText }}
        </div>

        <div class="time-row">
          {{ currentTime }} ✓✓
        </div>
      </div>

      <!-- BUTTONS -->
      <div v-if="buttons.length" class="button-list">
        <button
          v-for="(btn, index) in buttons"
          :key="index"
          class="wa-button"
          type="button"
        >
          <span v-if="btn.type === 'URL'">🔗</span>
          <span v-else-if="btn.type === 'PHONE_NUMBER'">📞</span>
          <span v-else>💬</span>
          {{ btn.text || btn.type }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

const props = defineProps({
  templateJson: {
    type: [String, Array],
    required: true,
  },

  headerMedia: {
    type: String,
    default: "",
  },

  documentFilename: {
    type: String,
    default: "document.pdf",
  },

  variables: {
    type: Object,
    default: () => ({}),
  },

  fallbackVariable: {
    type: String,
    default: "Sample",
  },
});

const mediaError = ref(false);

function safeParse(value) {
  try {
    if (!value) return [];
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return [];
  }
}

const components = computed(() => safeParse(props.templateJson));

function componentByType(type) {
  return components.value.find(
    c => String(c.type || "").toUpperCase() === type
  );
}

function replaceVars(text = "") {
  return String(text || "").replace(/{{\s*(\d+)\s*}}/g, (_, key) => {
    return props.variables?.[key] || props.fallbackVariable;
  });
}

const header = computed(() => {
  const h = componentByType("HEADER");
  if (!h) return { type: "NONE" };

  const format = String(h.format || "").toUpperCase();

  if (format === "IMAGE") {
    return {
      type: "IMAGE",
      url: props.headerMedia || h.example?.header_handle?.[0] || "",
    };
  }

  if (format === "VIDEO") {
    return {
      type: "VIDEO",
      url: props.headerMedia || h.example?.header_handle?.[0] || "",
    };
  }

  if (format === "DOCUMENT") {
    return {
      type: "DOCUMENT",
      url: props.headerMedia || h.example?.header_handle?.[0] || "",
      filename: props.documentFilename || h.example?.filename || "document.pdf",
    };
  }

  if (format === "TEXT") {
    return {
      type: "TEXT",
      text: h.text || "",
    };
  }

  return { type: "NONE" };
});



const bodyText = computed(() => {
  const b = componentByType("BODY");
  return b?.text || "";
});

const formattedBody = computed(() => {
  return replaceVars(bodyText.value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
});

const footerText = computed(() => {
  const f = componentByType("FOOTER");
  return replaceVars(f?.text || "");
});

const buttons = computed(() => {
  const b = componentByType("BUTTONS");
  return b?.buttons || [];
});

const currentTime = computed(() => {
  return new Date().toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
});
</script>

<style scoped>
.wa-phone-preview {
  width: 360px;
  max-width: 100%;
  border-radius: 28px;
  overflow: hidden;
  background: #111827;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  border: 8px solid #111827;
}

.wa-topbar {
  height: 64px;
  background: #075e54;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
}

.wa-topbar small {
  display: block;
  opacity: 0.8;
  font-size: 11px;
  margin-top: 2px;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #25d366;
  color: #064e3b;
  display: grid;
  place-items: center;
  font-weight: 900;
}

.wa-chat {
  min-height: 520px;
  padding: 18px 12px;
  background:
    linear-gradient(rgba(236, 229, 221, 0.92), rgba(236, 229, 221, 0.92)),
    repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.28) 0,
      rgba(255,255,255,0.28) 2px,
      transparent 2px,
      transparent 8px
    );
}

.wa-bubble {
  width: 88%;
  background: #ffffff;
  border-radius: 8px 8px 8px 0;
  padding: 7px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
}

.media {
  width: 100%;
  border-radius: 7px;
  overflow: hidden;
  background: #f1f5f9;
  margin-bottom: 8px;
}

.media img,
.media video {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: cover;
}

.doc-box {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px;
  background: #f1f5f9;
  border-radius: 7px;
  margin-bottom: 8px;
  overflow: hidden;
}

.doc-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #e2e8f0;
  display: grid;
  place-items: center;
  font-size: 22px;
}

.doc-box small {
  display: block;
  color: #64748b;
  margin-top: 3px;
}

.header-text {
  font-weight: 800;
  font-size: 14px;
  color: #111827;
  margin-bottom: 8px;
}

.body-text {
  color: #111827;
  font-size: 13px;
  line-height: 1.45;
  white-space: normal;
}

.footer-text {
  margin-top: 9px;
  color: #64748b;
  font-size: 12px;
}

.time-row {
  text-align: right;
  margin-top: 4px;
  color: #94a3b8;
  font-size: 10px;
}

.button-list {
  width: 88%;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.wa-button {
  border: none;
  background: #ffffff;
  color: #0084ff;
  font-weight: 700;
  font-size: 13px;
  padding: 11px;
  border-radius: 8px;
  cursor: pointer;
}

.media-error {
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 8px;
  font-size: 12px;
  margin-bottom: 8px;
}
</style>